import { useCallback, useMemo, useState } from 'react'

import { Wizard, WizardStepRenderParams } from '@factorialco/deprecated-design-system'
import { F0Box } from '@factorialco/f0-react'
import { Tabs, Spinner } from '@factorialco/f0-react/dist/experimental'
import {
  CurrencySelectorCurrencyFragment,
  FindWorkflowDocument,
  LoadVersionFormDocument,
  ProcurementTypeItemFragment,
} from 'generated/resources/graphql'

import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { formFromData } from 'modules/forms/hooks/useForms'
import AttachmentsStep from 'modules/procurement/components/purchaseRequests/NewRequestModal/Steps/AttachmentsStep'
import CustomFormSectionStep from 'modules/procurement/components/purchaseRequests/NewRequestModal/Steps/CustomFormSectionStep'
import GeneralInformationStep from 'modules/procurement/components/purchaseRequests/NewRequestModal/Steps/GeneralInformationStep'
import type { WizardData } from 'modules/procurement/components/purchaseRequests/NewRequestModal/types'
import { useProcurementHandlers } from 'modules/procurement/components/procurementTypes/Workflows/useProcurementHandlers'
import { generatePreviewFromForm } from 'modules/procurement/pages/RequestTypeDetailPage/helpers/generatePreview'
import { WorkflowContainer } from 'modules/workflows/components/WorkflowContainer'
import { WorkflowProvider } from 'modules/workflows/contexts/WorkflowProvider'

interface VersionPreviewProps {
  formId: string
  workflowId: string
  companyId: string
  requestType: ProcurementTypeItemFragment | null
  currencies: CurrencySelectorCurrencyFragment[]
  defaultCurrency?: string
}

const INITIAL_WIZARD_DATA: WizardData = {
  description: '',
  cost: '',
  currency: '',
  files: [],
  formAnswer: {},
}

export function VersionPreview({
  formId,
  workflowId,
  companyId,
  requestType,
  currencies,
  defaultCurrency,
}: VersionPreviewProps) {
  const [activeTab, setActiveTab] = useState('form')
  const handlers = useProcurementHandlers()

  const { data: workflowData, loading: workflowLoading } = useQuery(FindWorkflowDocument, {
    variables: { companyId, workflowId },
    skip: !workflowId,
  })

  const { data: formQueryData, loading: loadingForm } = useQuery(LoadVersionFormDocument, {
    variables: { id: String(formId), resourceType: 'Procurement::Type' },
    skip: !formId,
  })

  const formData = formQueryData?.forms?.formsConnection?.nodes?.[0]

  const buildForm = useCallback(() => {
    if (!formData) return undefined
    return formFromData({
      ...formData,
      resourceId: formData.resourceId ?? undefined,
      description: formData.description ?? '',
      authorFullName: formData.author?.employee?.fullName ?? '',
      authorAvatarUrl: formData.author?.employee?.avatar?.url ?? '',
      elements: formData.elements.map((el) => ({
        ...el,
        description: el.description ?? '',
      })),
    })
  }, [formData])

  const loadedForm = useMemo(() => {
    return !loadingForm ? buildForm() : undefined
  }, [loadingForm, buildForm])

  const previewType = useMemo(() => {
    if (!requestType || !loadedForm) return null
    return generatePreviewFromForm(requestType, loadedForm)
  }, [requestType, loadedForm])

  const formElements = previewType?.formsConnection?.nodes?.[0]?.elements ?? []

  const wizardSteps = useMemo(() => {
    const steps = []

    steps.push({
      title: i18n.t(
        'procurement.purchase_requests.new_request_wizard.steps.general_information.title'
      ),
      nextText: i18n.t(
        'procurement.purchase_requests.new_request_wizard.steps.general_information.next'
      ),
      render: (props: WizardStepRenderParams<WizardData>) => (
        <GeneralInformationStep
          {...props}
          defaultCurrency={defaultCurrency}
          currencies={currencies}
        />
      ),
      isCompleted: () => true,
    })

    const sections = formElements.filter((el) => el.type === 'section' && !el.params?.locked)
    const elementsWithoutSection = formElements.filter(
      (el) => el.type !== 'section' && !el.sectionId
    )

    if (sections.length > 0) {
      sections.forEach((section) => {
        const sectionElements = formElements.filter((el) => el.sectionId === section.id)
        steps.push({
          title: section.label,
          nextText: i18n.t(
            'procurement.purchase_requests.new_request_wizard.steps.custom_form_section_step.next'
          ),
          render: (props: WizardStepRenderParams<WizardData>) => (
            <CustomFormSectionStep
              {...props}
              elements={sectionElements}
              sectionTitle={section.label}
              sectionDescription={section.description}
            />
          ),
          isCompleted: () => true,
        })
      })
    }

    if (elementsWithoutSection.length > 0) {
      steps.push({
        title: i18n.t(
          'procurement.purchase_requests.new_request_wizard.steps.additional_information_step.title'
        ),
        nextText: i18n.t(
          'procurement.purchase_requests.new_request_wizard.steps.additional_information_step.next'
        ),
        render: (props: WizardStepRenderParams<WizardData>) => (
          <CustomFormSectionStep
            {...props}
            elements={elementsWithoutSection}
            sectionTitle={i18n.t(
              'procurement.purchase_requests.new_request_wizard.steps.additional_information_step.title'
            )}
            sectionDescription={i18n.t(
              'procurement.purchase_requests.new_request_wizard.steps.additional_information_step.description'
            )}
          />
        ),
        isCompleted: () => true,
      })
    }

    steps.push({
      title: i18n.t('procurement.purchase_requests.new_request_wizard.steps.attachments.title'),
      nextText: i18n.t('procurement.purchase_requests.new_request_wizard.steps.attachments.next'),
      render: (props: WizardStepRenderParams<WizardData>) => <AttachmentsStep {...props} />,
      isCompleted: () => false,
    })

    return steps
  }, [formElements, currencies, defaultCurrency])

  const workflow = workflowData?.workflows?.workflowsConnection?.nodes?.[0] ?? null

  return (
    <F0Box display="flex" flexDirection="column" width="full" height="full">
      <Tabs
        activeTabId={activeTab}
        tabs={[
          {
            id: 'form',
            label: i18n.t('procurement.purchase_requests.settings.types.detail.tabs.form.title'),
            onClick: () => setActiveTab('form'),
          },
          {
            id: 'workflows',
            label: i18n.t(
              'procurement.purchase_requests.settings.types.detail.tabs.workflows.title'
            ),
            onClick: () => setActiveTab('workflows'),
          },
        ]}
      />

      <F0Box display="flex" grow overflow="hidden" minHeight="0">
        {activeTab === 'form' && (
          <>
            {loadingForm ? (
              <F0Box display="flex" alignItems="center" justifyContent="center" grow>
                <Spinner />
              </F0Box>
            ) : previewType ? (
              <F0Box width="full" height="full" overflow="auto">
                <Wizard.Base<WizardData>
                  icon="document-edit"
                  title={i18n.t('procurement.purchase_requests.new_request_wizard.title_preview')}
                  initialData={INITIAL_WIZARD_DATA}
                  steps={wizardSteps}
                  onSubmit={() => Promise.resolve()}
                />
              </F0Box>
            ) : (
              <F0Box display="flex" alignItems="center" justifyContent="center" grow>
                {i18n.t('procurement.types.version_history.empty_state')}
              </F0Box>
            )}
          </>
        )}
        {activeTab === 'workflows' && (
          <>
            {workflowLoading ? (
              <F0Box display="flex" alignItems="center" justifyContent="center" grow>
                <Spinner />
              </F0Box>
            ) : workflow ? (
              <WorkflowProvider>
                <F0Box width="full" grow overflow="hidden" minHeight="0">
                  <WorkflowContainer
                    workflow={workflow}
                    handlers={handlers}
                    variables={{}}
                    readonly
                    trigger={{
                      title: i18n.t(
                        'procurement.purchase_requests.settings.types.detail.tabs.workflows.trigger.title'
                      ),
                      description: i18n.t(
                        'procurement.purchase_requests.settings.types.detail.tabs.workflows.trigger.description'
                      ),
                    }}
                  />
                </F0Box>
              </WorkflowProvider>
            ) : (
              <F0Box display="flex" alignItems="center" justifyContent="center" grow>
                {i18n.t('procurement.types.version_history.no_workflow')}
              </F0Box>
            )}
          </>
        )}
      </F0Box>
    </F0Box>
  )
}
